import { FormControlLabel, MenuItem, Stack, Switch, Typography } from "@mui/material";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useEffect, useMemo, useRef, useState } from "react";
import { mediaService } from "../../../Core/Api/services";
import { AclTypes } from "../../../Core/Api/Types/acl-types";
import { BasketItemStatus } from "../../../Core/Api/Types/basket-item-status";
import { DbFileInfos } from "../../../Core/Api/Types/db-file-infos";
import { DbSequenceMetadataValueInfos } from "../../../Core/Api/Types/db-sequence-metadata-value-infos";
import { FileLinkedType } from "../../../Core/Api/Types/file-linked-type";
import { OrderProductType } from "../../../Core/Api/Types/order-product-type";
import {
  TranskodeYoutubeChannelDelivery,
  TranskodeYoutubeChannelDeliveryPolicy,
} from "../../../Core/Api/Types/transkode-youtube-channel-delivery";
import { GetMetadataByTagName } from "../../../Core/Utils/asset-object-utils";
import GlobalConst from "../../../Core/Utils/Constants/globals";
import { ConfigBaseProps } from "../../../Pages/Basket/basket";
import {
  ItemField,
  SettingRootField,
  UpdateItemRootOnBasket,
  UpdateItemSettingsRootOnBasket,
  UpdateItemStatusOnBasket,
} from "../../../Redux/Reducers/redux-slice-basket";
import { FormExists, GetFormIsValid, GetSectionIsValid } from "../../../Redux/Reducers/redux-slice-form-validation";
import { useAppDispatch, useAppSelector } from "../../../Redux/redux-store";
import {
  containsOnlyDigits,
  ConvertSecondsToHHMMSS,
  GetOrderProductReadableType,
  UserHasAcl,
} from "../../../Utils/helpers";
import { TimecodeToSec } from "../../../Utils/Timecode/ReactPlayer";
import FormAutocomplete from "../../Forms/FormAutocomplete/form-autocomplete";
import FormInputDateTime from "../../Forms/FormInputDate/form-input-date-time";
import FormInputNumber from "../../Forms/FormInputNumber/form-input-number";
import FormInputSelect from "../../Forms/FormInputSelect/form-input-select";
import FormInputText from "../../Forms/FormInputText/form-input-text";
import FormSection from "../../Forms/FormSection/form-section";
import ConfigCommonSectionFormScheduling from "../ConfigCommon/config-common-section-form-scheduling";
import ConfigFormIcon from "../ConfigFormIcon";
import ConfigHeader from "../ConfigHeader/config-header";
import ConfigSaveButton from "../ConfigSaveButton/config-save-button";
import { useTranslation } from "react-i18next";
import UploadCard, { UploadCardHandle, UploadContext } from "../../Cards/UploadCard/upload-card";
import { ObjectType } from "../../../Core/Api/Types/object-type";

interface ConfigYoutubeChannelDeliveryProps extends ConfigBaseProps {}

function updateDateString(originalString: string, newDate: Date) {
  // Format the new date as "DDMMYYYY" using Day.js
  const newDateString = dayjs(newDate).format("DDMMYYYY");

  // Use regular expression to replace the date in the original string
  const updatedString = originalString.replace(/\d{8}/, newDateString);

  return updatedString;
}

const ConfigYoutubeChannelDelivery: React.FC<ConfigYoutubeChannelDeliveryProps> = (props) => {
  const { t } = useTranslation();
  dayjs.extend(utc);
  const { handleSubmit, handleCancel, handleChangeProduct, item, ...otherProps } = props;
  const [files, setFiles] = useState<DbFileInfos[]>([]);
  const formId = `form-youtube-channel-delivery-${item.id}`;
  const formSectionYoutubeChannelId = `youtube-channel`;
  const formSectionYoutubeVideoId = `youtube-video`;
  const formSectionDeliverySettingsId = `delivery-settings`;
  const formValid = useAppSelector(GetFormIsValid(formId));
  const formExists = useAppSelector(FormExists(formId));
  const dispatch = useAppDispatch();
  const [adBreakTimesMinutes, setAdBreakTimesMinutes] = useState<number>(0);

  const userCanScheduleOrder = useMemo(() => UserHasAcl(AclTypes.aclOrderSchedule), []);
  const uploadCardRef = useRef<UploadCardHandle>(null);
  const settings: TranskodeYoutubeChannelDelivery = item.selectedProduct?.settings;

  // Update settings
  const handleUpdateParam = (field: SettingRootField, value: any) => {
    dispatch(UpdateItemSettingsRootOnBasket({ itemId: item.id, field: field, value: value }));
  };

  const handleUpdateItemRoot = (field: ItemField, value: any) => {
    dispatch(UpdateItemRootOnBasket({ itemId: item.id, field: field, value: value }));
  };

  const formValidTrigger = useMemo(() => {
    // In case of invalid, update item status
    if (!formValid && formExists && item.status !== BasketItemStatus.OptionsDone)
      dispatch(UpdateItemStatusOnBasket({ itemId: item.id, status: BasketItemStatus.New }));
    return formValid;
  }, [formValid]);

  const formSectionYtChannel = useAppSelector(GetSectionIsValid(formId, formSectionYoutubeChannelId));
  const formSectionYtVideo = useAppSelector(GetSectionIsValid(formId, formSectionYoutubeVideoId));
  const formSectionDelivery = useAppSelector(GetSectionIsValid(formId, formSectionDeliverySettingsId));

  const selectedChannel = useMemo(() => {
    if (!settings.channel) return null;

    return settings?.channels?.find((x) => x.channelId === settings.channel) || null;
  }, [settings.channel]);

  const validationAdBreakTimes = (value: string): boolean => {
    let result: boolean = true;
    let breaks: string[] = value.split("|");
    let metadata: DbSequenceMetadataValueInfos | null = GetMetadataByTagName(item.assetInfos, "sequenceSegments");

    if (breaks.length === 0 || !metadata) return true;

    let MaxDuration = TimecodeToSec(metadata.timecodeDuration!, 25);

    breaks.forEach((element) => {
      if (element !== "-1") {
        if (element.includes(":")) {
          if (MaxDuration < TimecodeToSec(element, 25)) {
            result = false;
          }
        } else if (Number.isNaN(Number.parseInt(element)) || !containsOnlyDigits(element)) {
          result = false;
        } else if (MaxDuration < Number.parseInt(element)) {
          result = false;
        }
      }
    });
    return result;
  };

  /// Generate ad break times string, from value in minutes.
  const generateAdBreakTimes = (value: number) => {
    let metadata: DbSequenceMetadataValueInfos | null = GetMetadataByTagName(item.assetInfos, "sequenceSegments");

    if (value === 0 || !metadata || Number.isNaN(value)) return;

    let maxDuration = TimecodeToSec(metadata.timecodeDuration!, 25) / 60;

    if (value >= maxDuration) return;

    let result: string = "";
    for (let i = 1; i <= maxDuration / value; i++) {
      const durationInSeconds = value * i * 60;
      result += `${ConvertSecondsToHHMMSS(durationInSeconds)}|`;
    }

    result = result.slice(0, -1);
    handleUpdateParam(SettingRootField.YoutubeDeliveryAdBreakTimes, result);
  };

  const handleKeywordsValueInput = (value: string[]) => {
    return value.reduce((acc: string[], str) => {
      if (str.includes("|")) return acc.concat(str.split("|"));
      else if (str.includes(",")) return acc.concat(str.split(","));
      else return acc.concat(str);
    }, []);
  };

  useEffect(() => {
    if (item.titleId)
      mediaService.GetFilesByObjectId(item.titleId).then(function (response) {
        if (response && response.files) setFiles(response.files);
      });
  }, []);

  useEffect(() => {
    let customId = "";
    if (item.scheduleTime) {
      customId = updateDateString(settings.customId || "N/A", item.scheduleTime);
    } else if (settings.startTime && settings.startTime.length) {
      customId = updateDateString(settings.customId || "N/A", dayjs(settings.startTime).toDate());
    } else {
      customId = updateDateString(settings.customId || "N/A", new Date());
    }
    handleUpdateParam(SettingRootField.YoutubeDeliveryCustomId, customId);
  }, [settings.startTime, item.scheduleTime]);

  return (
    <Stack width={"100%"} {...otherProps} display={formExists ? "" : "none"}>
      <ConfigHeader
        configType={item.selectedProduct?.type ? item.selectedProduct?.type : OrderProductType.TranscodeDcp}
        title={GetOrderProductReadableType(
          item.selectedProduct?.type ? item.selectedProduct?.type : OrderProductType.TranscodeDcp
        )}
        handleCancel={handleCancel}
        handleChangeProduct={handleChangeProduct}
        itemId={item.id}
      />
      <Stack spacing={1.5} width={"100%"} {...otherProps}>
        <FormSection
          icon={<ConfigFormIcon icon="settings" />}
          title={t("common:youtubeChannel")}
          status={formSectionYtChannel ? "complete" : "warning"}
          statusText={formSectionYtChannel ? t("common:configured") : t("common:configurationRequired")}
        >
          <FormAutocomplete
            label={t("common:youtubeChannel")}
            fullWidth={true}
            options={[...(settings?.channels || [])]?.sort((a, b) => a.name!.localeCompare(b.name!)) || []}
            value={selectedChannel}
            placeholder={t("field:placeholder.selectYoutubeChannel")}
            getOptionLabel={(option: any) => option.name + " (channel Id #" + option.channelId + ")"}
            isOptionEqualToValue={(option, value) => option.channelId === value?.channelId}
            onChange={(v) => {
              handleUpdateParam(SettingRootField.YoutubeDeliveryChannel, v?.channelId);
              handleUpdateParam(SettingRootField.YoutubeDeliveryDescription, v?.description);
              handleUpdateParam(SettingRootField.YoutubeDeliveryMovieLabels, v?.assetLabels);
              handleUpdateParam(SettingRootField.YoutubeDeliveryKeywords, v?.keywords);
              handleUpdateParam(SettingRootField.YoutubeDeliveryIsMadeForKids, v?.isMadeForKids);
              handleUpdateParam(SettingRootField.YoutubeDeliveryPreRoll, v?.preRoll);
              handleUpdateParam(SettingRootField.YoutubeDeliveryPostRoll, v?.endRoll);
              handleUpdateParam(SettingRootField.YoutubeDeliveryTerritoriesCode, v?.ownership);
              handleUpdateParam(SettingRootField.YoutubeDeliveryUsagePolicy, v?.usagePolicy);
              handleUpdateParam(SettingRootField.YoutubeDeliveryPlaylistIds, v?.playlistId);
              handleUpdateParam(SettingRootField.YoutubeDeliveryNotifySubscribers, v?.notifySubscribers);
              handleUpdateParam(SettingRootField.YoutubeDeliveryBlockOutsideOwnership, v?.blockOutsideOwnership);
              setAdBreakTimesMinutes(v?.adBreakTime);
              generateAdBreakTimes(v?.adBreakTime);
            }}
            validationProps={{
              formId: formId,
              formSectionId: formSectionYoutubeChannelId,
              fieldId: "input-channel",
              required: true,
              initialState: false,
            }}
          />
        </FormSection>

        <FormSection
          icon={<ConfigFormIcon icon="video" />}
          title={t("form:section.title.videoSettings")}
          status={formSectionYtVideo ? "complete" : "warning"}
          statusText={formSectionYtVideo ? t("common:configured") : t("common:configurationRequired")}
        >
          <Stack spacing={2}>
            <FormInputText
              label={t("field:label.customVideoId")}
              value={settings.customId}
              onChange={(e) => {
                handleUpdateParam(SettingRootField.YoutubeDeliveryCustomId, e.target.value);
              }}
              validationProps={{
                formId: formId,
                formSectionId: formSectionYoutubeVideoId,
                fieldId: "input-video-custom-id",
                required: true,
                initialState: false,
                maxLength: 64,
              }}
            />
            <FormInputText
              label={t("field:label.videoTitle")}
              value={settings.title}
              onChange={(e) => {
                handleUpdateParam(SettingRootField.YoutubeDeliveryTitle, e.target.value);
              }}
              validationProps={{
                formId: formId,
                formSectionId: formSectionYoutubeVideoId,
                fieldId: "input-video-title",
                required: true,
                initialState: false,
                maxLength: 100,
              }}
            />
            <FormInputText
              label={t("field:label.videoDescription")}
              value={settings.description}
              multiline
              rows={4}
              onChange={(e) => {
                handleUpdateParam(SettingRootField.YoutubeDeliveryDescription, e.target.value);
              }}
              validationProps={{
                formId: formId,
                formSectionId: formSectionYoutubeVideoId,
                fieldId: "input-video-description",
                required: false,
                initialState: false,
                maxLength: 5000,
              }}
            />
            <FormAutocomplete
              label={t("field:label.assetLabels")}
              freeSolo
              multiple
              fullWidth={true}
              options={[]}
              value={settings?.movieLabels || []}
              placeholder={t("field:placeholder.assetLabels")}
              onChange={(v) => {
                handleUpdateParam(SettingRootField.YoutubeDeliveryMovieLabels, v);
              }}
              validationProps={{
                formId: formId,
                formSectionId: formSectionYoutubeVideoId,
                fieldId: "input-movie-labels",
                required: false,
                initialState: false,
              }}
            />
            <FormAutocomplete
              label={t("field:label.videoKeywords")}
              freeSolo
              multiple
              fullWidth={true}
              options={[]}
              value={settings?.keywords || []}
              placeholder={t("field:placeholder.videoKeywords")}
              onChange={(v) => {
                handleUpdateParam(SettingRootField.YoutubeDeliveryKeywords, handleKeywordsValueInput(v));
              }}
              validationProps={{
                formId: formId,
                formSectionId: formSectionYoutubeVideoId,
                fieldId: "input-video-keywords",
                required: false,
                initialState: false,
              }}
            />
            <Typography color="text.tier">
              <FormControlLabel
                sx={{ ml: 0 }}
                control={
                  <Switch
                    size="small"
                    checked={settings.isMadeForKids}
                    onChange={() =>
                      handleUpdateParam(SettingRootField.YoutubeDeliveryIsMadeForKids, !settings.isMadeForKids)
                    }
                  />
                }
                label={t("field:label.isVideoForKids")}
              />
            </Typography>
            <FormInputNumber
              label={t("field:label.adBreakTimesMinutes")}
              value={adBreakTimesMinutes}
              stackProps={{}}
              onChange={(e) => {
                if (Number.parseInt(e.target.value) < 0) return;
                setAdBreakTimesMinutes(Number.parseInt(e.target.value));
                generateAdBreakTimes(Number.parseInt(e.target.value));
              }}
              validationProps={{
                formId: formId,
                formSectionId: formSectionYoutubeVideoId,
                fieldId: "input-video-AddBreakTimes",
                required: false,
                initialState: false,
                minValue: 0,
              }}
            ></FormInputNumber>
            <FormInputText
              label={t("field:label.adBreakTimes")}
              value={settings.adBreakTimes}
              onChange={(e) => {
                setAdBreakTimesMinutes(0);
                handleUpdateParam(SettingRootField.YoutubeDeliveryAdBreakTimes, e.target.value);
              }}
              validationProps={{
                formId: formId,
                formSectionId: formSectionYoutubeVideoId,
                fieldId: "input-video-AdBreakTimes",
                required: false,
                initialState: false,
                maxLength: 5000,
                validationFunction: validationAdBreakTimes,
              }}
            />
            <Stack spacing={1.5} direction="row" justifyContent="space-between">
              <Typography color="text.tier">
                <FormControlLabel
                  sx={{ ml: 0 }}
                  control={
                    <Switch
                      size="small"
                      checked={settings.preRoll}
                      disabled={settings.adBreakTimes === null || settings.adBreakTimes === ""}
                      onChange={() => handleUpdateParam(SettingRootField.YoutubeDeliveryPreRoll, !settings.preRoll)}
                    />
                  }
                  label={t("field:label.preRoll")}
                />
              </Typography>
              <Typography color="text.tier">
                <FormControlLabel
                  sx={{ ml: 0 }}
                  control={
                    <Switch
                      size="small"
                      checked={settings.postRoll}
                      disabled={settings.adBreakTimes === null || settings.adBreakTimes === ""}
                      onChange={() => handleUpdateParam(SettingRootField.YoutubeDeliveryPostRoll, !settings.postRoll)}
                    />
                  }
                  label={t("field:label.postRoll")}
                />
              </Typography>
            </Stack>
            <FormAutocomplete
              label={t("common:thumbnail")}
              options={files.filter((x) => x.linkedType === FileLinkedType.Thumbnail)}
              value={{ name: settings.customThumbnail }}
              getOptionLabel={(option: any) => option.name || ""}
              placeholder={t("field:placeholder.selectThumbnail")}
              isOptionEqualToValue={(option, value) => option.name === value.name}
              onChange={(v) => {
                uploadCardRef.current?.performRemove();
                if (v === null) {
                  handleUpdateParam(SettingRootField.YoutubeDeliveryCustomThumbnail, null);
                  handleUpdateParam(SettingRootField.YoutubeDeliveryThumbnailFileId, null);
                } else {
                  handleUpdateParam(SettingRootField.YoutubeDeliveryCustomThumbnail, v.name);
                  handleUpdateParam(SettingRootField.YoutubeDeliveryThumbnailFileId, v.id);
                }
              }}
              validationProps={{
                formId: formId,
                formSectionId: formSectionYoutubeChannelId,
                fieldId: "input-video-thumbnail",
                required: false,
                initialState: false,
              }}
            />
            <UploadCard
              ref={uploadCardRef}
              title={t("common:thumbnail")}
              userHasRightToDelete={false}
              context={UploadContext.ThumbnailBase64}
              objectId={1}
              uploadButtonInCard={true}
              disableButton={settings.customThumbnail !== null}
              format={{
                id: 0,
                isUploaded: false,
                maxFileSizeMb: 10,
                name: "thumbnail",
                userHasRighToDelete: false,
                acceptsFiles: { ["application/octet-stream"]: [".jpg", ".jpeg", ".png"] },
                isUserDataMandatory: false,
                objectType: ObjectType.Image,
              }}
              onSuccessUpload={(file) => handleUpdateParam(SettingRootField.YoutubeDeliveryCustomThumbnail, file)}
            />
          </Stack>
        </FormSection>

        <FormSection
          icon={<ConfigFormIcon icon="delivery" />}
          title={t("form:section.title.deliverySettings")}
          status={formSectionDelivery ? "complete" : "warning"}
          statusText={formSectionDelivery ? t("common:configured") : t("common:configurationRequired")}
        >
          <Stack spacing={2}>
            <FormAutocomplete
              label={t("field:label.territoriesCodes")}
              freeSolo
              multiple
              fullWidth={true}
              options={[]}
              value={settings?.territoriesCodes || []}
              placeholder={t("field:placeholder.enterTerritoriesCodes")}
              onChange={(v) => {
                handleUpdateParam(SettingRootField.YoutubeDeliveryTerritoriesCode, v);
              }}
              sx={{ maxWidth: "683px" }}
              validationProps={{
                formId: formId,
                formSectionId: formSectionDeliverySettingsId,
                fieldId: "input-territories-codes",
                required: true,
                initialState: false,
              }}
            />
            <FormInputSelect
              label={t("field:label.usagePolicy")}
              stackProps={{}}
              value={settings.usagePolicy}
              onChange={(e) => handleUpdateParam(SettingRootField.YoutubeDeliveryUsagePolicy, e.target.value)}
              validationProps={{
                formId: formId,
                formSectionId: formSectionDeliverySettingsId,
                fieldId: "input-usage-policy",
                required: true,
                initialState: true,
              }}
            >
              {Object.keys(TranskodeYoutubeChannelDeliveryPolicy).map((key) => (
                <MenuItem key={key} value={key}>
                  {key}
                </MenuItem>
              ))}
            </FormInputSelect>
            <FormAutocomplete
              label={t("field:label.addVideoToPlaylists")}
              freeSolo
              multiple
              fullWidth={true}
              options={[]}
              value={settings?.playlistIds || []}
              placeholder={t("field:placeholder.enterPlaylistId")}
              onChange={(v) => {
                handleUpdateParam(SettingRootField.YoutubeDeliveryPlaylistIds, v);
              }}
              validationProps={{
                formId: formId,
                formSectionId: formSectionDeliverySettingsId,
                fieldId: "input-delivery-playlist",
                required: false,
                initialState: false,
              }}
            />
            <FormInputDateTime
              label={t("field:label.youtubeStartTime")}
              format={GlobalConst.MOMENT_DATE_FORMAT_FULL}
              value={dayjs(settings.startTime).utc(true).local()} // chinese feint utc(true) https://day.js.org/docs/en/manipulate/utc
              onChange={(value) =>
                handleUpdateParam(
                  SettingRootField.YoutubeDeliveryStartTime,
                  dayjs(value).utc().format(GlobalConst.MOMENT_DATE_FORMAT_FULL)
                )
              }
              onReset={() => handleUpdateParam(SettingRootField.YoutubeDeliveryStartTime, null)}
              stackProps={{ mt: 1 }}
              validationProps={{
                formId: formId,
                formSectionId: formSectionDeliverySettingsId,
                fieldId: "youtube-start-time",
                initialState: true,
                initialValue: settings.startTime ? dayjs(settings.endTime).toDate() : undefined,
                required: false,
                onlyInFuture: settings.startTime !== null,
              }}
            />
            <FormInputDateTime
              label={t("field:label.youtubeEndTime")}
              format={GlobalConst.MOMENT_DATE_FORMAT_FULL}
              value={dayjs(settings.endTime).utc(true).local()}
              onChange={(value) =>
                handleUpdateParam(
                  SettingRootField.YoutubeDeliveryEndTime,
                  dayjs(value).utc().format(GlobalConst.MOMENT_DATE_FORMAT_FULL)
                )
              }
              onReset={() => handleUpdateParam(SettingRootField.YoutubeDeliveryEndTime, null)}
              stackProps={{ mt: 1 }}
              validationProps={{
                formId: formId,
                formSectionId: formSectionDeliverySettingsId,
                fieldId: "youtube-end-time",
                initialState: true,
                initialValue: settings.endTime ? dayjs(settings.endTime).toDate() : undefined,
                required: false,
                onlyInFuture: settings.endTime !== null,
              }}
            />
            <Stack spacing={1} direction="row" justifyContent="space-between">
              <Typography color="text.tier">
                <FormControlLabel
                  sx={{ ml: 0 }}
                  control={
                    <Switch
                      size="small"
                      checked={settings.notifySubscribers}
                      onChange={() =>
                        handleUpdateParam(
                          SettingRootField.YoutubeDeliveryNotifySubscribers,
                          !settings.notifySubscribers
                        )
                      }
                    />
                  }
                  label={t("field:label.notifySubscribers")}
                />
              </Typography>
              <Typography color="text.tier">
                <FormControlLabel
                  sx={{ ml: 0 }}
                  control={
                    <Switch
                      size="small"
                      checked={settings.blockOutsideOwnership}
                      onChange={() =>
                        handleUpdateParam(
                          SettingRootField.YoutubeDeliveryBlockOutsideOwnership,
                          !settings.blockOutsideOwnership
                        )
                      }
                    />
                  }
                  label={t("field:label.blockOutsideTerritories")}
                />
              </Typography>
            </Stack>
          </Stack>
        </FormSection>
        <ConfigCommonSectionFormScheduling item={item} formid={formId} />
      </Stack>
      <ConfigSaveButton item={item} onSubmit={handleSubmit} disabled={!formValidTrigger} />
    </Stack>
  );
};

export default ConfigYoutubeChannelDelivery;
